import { useOutletContext, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowUpRight } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { buildPath } from "@/lib/routes";
import { useLang } from "@/hooks/useLang";
import Seo from "@/components/Seo";
import PageHero from "@/components/PageHero";
import { Stagger, StaggerItem } from "@/components/motion/Reveal";
import LeadMagnet from "@/components/LeadMagnet";
import { breadcrumbSchema } from "@/lib/schema";

export default function ServicesHub() {
  const { lang, t } = useLang();
  const { profile } = useOutletContext();
  const { data: services = [] } = useQuery({ queryKey: ["services"], queryFn: api.getServices });

  const meta = {
    da: { title: "SEO-ydelser | Magnus Løv Schmidt", h: ["SEO-ydelser der dækker", "hele rejsen"], d: "Fra teknisk SEO og on-page til linkbuilding, ecommerce og strategi. Vælg den disciplin, der matcher din udfordring." },
    en: { title: "Danish SEO services | Magnus Løv Schmidt", h: ["Danish SEO services that", "cover the whole journey"], d: "From technical SEO and on-page to link building, ecommerce and strategy. Danish SEO expertise — pick the discipline that matches your challenge." },
  }[lang];

  return (
    <>
      <Seo routeKey="services" lang={lang} title={meta.title} description={meta.d}
        jsonLd={[breadcrumbSchema([{ name: t("nav_home"), path: buildPath("home", lang) }, { name: t("nav_services"), path: buildPath("services", lang) }])]} />
      <PageHero title={meta.h} description={meta.d}
        crumbs={[{ label: t("nav_home"), to: "home" }, { label: t("nav_services") }]} />

      <section data-testid="hub-intro" className="px-6 md:px-12 lg:px-20 pt-12 -mb-4">
        <div className="max-w-3xl space-y-4 text-muted-foreground font-light leading-relaxed">
          <p>{lang === "da"
            ? "SEO er ikke én disciplin, men mange, der spiller sammen. Nogle forretninger mangler et teknisk fundament, andre autoritet eller det rigtige indhold. Derfor tilbyder jeg specialiserede ydelser, du kan kombinere efter behov — altid med udgangspunkt i, hvad der reelt flytter din forretning."
            : "SEO isn't one discipline, but many working together. Some businesses lack a technical foundation, others authority or the right content. That's why I offer specialised services you can combine as needed — always based on what genuinely moves your business."}</p>
          <p>{lang === "da"
            ? "Er du i tvivl om, hvor du skal starte, begynder jeg med en SEO-strategi, der prioriterer indsatsen. Herfra eksekverer jeg på det, der giver mest værdi først."
            : "If you're unsure where to start, we begin with an SEO strategy that prioritises the effort. From there we execute on what delivers the most value first."}</p>
        </div>
      </section>

      <section className="px-6 md:px-12 lg:px-20 py-16">
        <Stagger className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {services.map((s, i) => {
            const c = s[lang] || s.da;
            return (
              <StaggerItem key={s.slug}>
                <Link to={buildPath("service", lang, { slug: s.slug })} data-testid={`service-card-${s.slug}`}
                  className="card-hover group flex h-full flex-col rounded-xl border border-border bg-card p-7">
                  <div className="flex items-center justify-between">
                    <span className="font-mono text-xs text-muted-foreground">{String(i + 1).padStart(2, "0")}</span>
                    <ArrowUpRight size={20} className="text-muted-foreground transition-all group-hover:text-primary group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </div>
                  <h2 className="mt-6 font-heading text-2xl font-bold tracking-tight">{c.title}</h2>
                  <p className="mt-3 flex-1 text-sm text-muted-foreground font-light leading-relaxed">{c.tagline}</p>
                  <span className="mt-5 label-mono text-primary">{t("read_more")}</span>
                </Link>
              </StaggerItem>
            );
          })}
        </Stagger>
      </section>
      <LeadMagnet profile={profile} />
    </>
  );
}
